import { useState, useEffect, useRef, useCallback } from "react";
import {
  useLoaderData,
  useRevalidator,
  useSearchParams,
  useFetcher,
  useNavigate,
} from "react-router-dom";
import { CourseDto } from "./types/coursesDTO";
import { CoursesFilter } from "./types/coursesFilter";
import { CourseCard } from "./components/courseCard";
import { CoursesFilterComponent } from "./components/coursesFilterComponent";
import { Level } from "./enums/level";
import { CoursesLoaderData } from "@/routes/courses/types/coursesLoaderData.ts";
import { BaseFilterOptionResponse } from "@/types/apiTypes/baseFilterOptionResponse.ts";
import { basketService } from "@/services/basket/basketService";
import { authService } from "@/services/auth/authService";
import { Card, CardContent } from "@/components/ui/card";
import { BookOpen } from "lucide-react";
import { useTranslation } from "react-i18next";

export default function CoursesPage() {
  const { t } = useTranslation();
  const initialData = useLoaderData() as CoursesLoaderData;
  const revalidator = useRevalidator();
  const [searchParams, setSearchParams] = useSearchParams();
  const fetcher = useFetcher<CoursesLoaderData>();
  const navigate = useNavigate();

  // State'ler
  const [allCourses, setAllCourses] = useState<CourseDto[]>(
    initialData.courses.items,
  );
  const [pagination, setPagination] = useState(initialData.courses);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasAppliedNewFilters, setHasAppliedNewFilters] = useState(false);

  // Filter seçenekleri - ilk yüklemede gelir, sonra state'te tutulur
  const [filterOptions, setFilterOptions] = useState<{
    categories: BaseFilterOptionResponse[];
    tags: BaseFilterOptionResponse[];
  }>({
    categories: initialData.filterOptions?.categories || [],
    tags: initialData.filterOptions?.tags || [],
  });

  // Max price - ilk yüklemede gelir, sonra state'te tutulur
  const [maxPrice, setMaxPrice] = useState<number>(
    initialData.maxPrice ?? 10000,
  );

  // Intersection Observer için ref
  const loadMoreRef = useRef<HTMLDivElement>(null);

  // Sayfa yüklendiğinde URL'deki page parametresini temizle
  useEffect(() => {
    const currentPage = searchParams.get("page");
    if (
      (currentPage && parseInt(currentPage) > 1) ||
      searchParams.get("_internal")
    ) {
      // URL'den page parametresini kaldır ama diğer filtreleri koru
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete("page");
      newSearchParams.delete("pageSize");
      newSearchParams.delete("_internal");

      // URL'i güncelle (history replace ile - back butonunu etkilemez)
      navigate(`?${newSearchParams.toString()}`, { replace: true });
    }
  }, []); // Sadece component mount olduğunda çalışır

  // URL'den filtreleri oku
  const getFiltersFromURL = (): CoursesFilter => {
    return {
      desc: true,
      orderBy: "CreatedAt",
      pageNumber: 0,
      pageSize: 0,
      categories: searchParams.getAll("category"),
      tags: searchParams.getAll("tag"),
      minPrice: searchParams.get("minPrice")
        ? Number(searchParams.get("minPrice"))
        : null,
      maxPrice: searchParams.get("maxPrice")
        ? Number(searchParams.get("maxPrice"))
        : null,
      search: searchParams.get("search") || "",
      levels: searchParams.getAll("level").map((l) => Number(l) as Level),
    };
  };

  const [filters, setFilters] = useState<CoursesFilter>(getFiltersFromURL());

  // Filtreleri URL'e kaydet ve loader'ı yeniden çağır
  const applyFilters = () => {
    const newSearchParams = new URLSearchParams();

    // Filtreleri URL parametrelerine dönüştür
    (filters.categories || []).forEach((cat) =>
      newSearchParams.append("category", cat),
    );
    (filters.tags || []).forEach((tag) => newSearchParams.append("tag", tag));
    (filters.levels || []).forEach((level) =>
      newSearchParams.append("level", level.toString()),
    );

    if (filters.search) newSearchParams.set("search", filters.search);
    if (filters.minPrice !== null)
      newSearchParams.set("minPrice", filters.minPrice.toString());
    if (filters.maxPrice !== null)
      newSearchParams.set("maxPrice", filters.maxPrice.toString());

    // Page parametresini URL'e EKLEMEME - infinite scroll için gerekli değil
    // newSearchParams.set("page", "1");
    // newSearchParams.set("pageSize", "10");

    // URL'i güncelle
    newSearchParams.set("_internal", "true");
    setSearchParams(newSearchParams);
    setHasAppliedNewFilters(true);

    // Loader'ı yeniden çağır
    //revalidator.revalidate();
  };

  const resetFilters = () => {
    const emptyFilters: CoursesFilter = {
      categories: [],
      tags: [],
      minPrice: null,
      maxPrice: null,
      search: "",
      levels: [],
      pageNumber: 0,
      pageSize: 0,
      desc: null,
      orderBy: null,
    };
    setFilters(emptyFilters);
    const currentSearchParams = new URLSearchParams();
    currentSearchParams.set("_internal", "true");
    setSearchParams(currentSearchParams);
    setHasAppliedNewFilters(true);
  };

  // Daha fazla veri yükle
  const loadMoreCourses = useCallback(() => {
    if (
      isLoadingMore ||
      !pagination.hasNextPage ||
      fetcher.state === "loading"
    ) {
      return;
    }

    setIsLoadingMore(true);

    const currentSearchParams = new URLSearchParams(searchParams);
    currentSearchParams.set("page", (pagination.currentPage + 1).toString());
    currentSearchParams.set("pageSize", "5");
    // Internal fetch olduğunu belirt
    currentSearchParams.set("_internal", "true");

    fetcher.load(`/courses?${currentSearchParams.toString()}`);
  }, [
    isLoadingMore,
    pagination.hasNextPage,
    pagination.currentPage,
    searchParams,
    fetcher,
  ]);

  // Fetcher sonuçlarını işle
  useEffect(() => {
    if (fetcher.data?.courses && fetcher.state === "idle" && isLoadingMore) {
      setAllCourses((prev) => [
        ...prev,
        ...(fetcher.data?.courses?.items ?? []),
      ]);
      setPagination(fetcher.data?.courses);
      setIsLoadingMore(false);
    }
  }, [fetcher.data, fetcher.state, isLoadingMore]);

  // Yeni filtre uygulandığında kurs listesini sıfırla
  useEffect(() => {
    if (revalidator.state === "idle") {
      setAllCourses(initialData.courses.items);
      setPagination(initialData.courses);
      setHasAppliedNewFilters(false);
      // Filter seçenekleri varsa güncelle (ilk yüklemede)
      if (initialData.filterOptions) {
        setFilterOptions({
          categories: initialData.filterOptions.categories,
          tags: initialData.filterOptions.tags,
        });
      }
      // Max price varsa güncelle
      if (initialData.maxPrice) {
        setMaxPrice(initialData.maxPrice);
      }
    }
  }, [initialData]);

  // Intersection Observer için effect
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const target = entries[0];
        if (target.isIntersecting && pagination.hasNextPage) {
          loadMoreCourses();
        }
      },
      {
        threshold: 0.1,
        rootMargin: "50px",
      },
    );

    const currentRef = loadMoreRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [loadMoreCourses, pagination.hasNextPage]);

  const isAuthenticated = !!authService.getAccessToken();

  const handleAddToBasket = async (courseId: string) => {
    if (!authService.getAccessToken()) {
      navigate("/login");
      return;
    }
    await basketService.addItemToBasket({
      courseId: courseId,
      basketId: null,
    });
    revalidator.revalidate();
  };

  // Check if any filters are applied
  const hasActiveFilters = () => {
    return (
      (filters.categories?.length || 0) > 0 ||
      (filters.tags?.length || 0) > 0 ||
      (filters.levels?.length || 0) > 0 ||
      (filters.search?.length || 0) > 0 ||
      filters.minPrice !== null ||
      filters.maxPrice !== null
    );
  };

  return (
    <div className="min-h-screen">
      <div className="container mx-auto px-4 py-6 md:py-8">
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Filter Sidebar */}
          <div className="lg:w-80 flex-shrink-0">
            <div className="lg:sticky lg:top-6">
              <CoursesFilterComponent
                filters={filters}
                onFiltersChange={setFilters}
                onApplyFilters={applyFilters}
                onResetFilters={resetFilters}
                isLoading={revalidator.state === "loading"}
                availableCategories={filterOptions.categories ?? []}
                availableTags={filterOptions.tags ?? []}
                maxPrice={maxPrice}
              />
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1 min-w-0">
            {/* Active Filters Summary - Mobile */}
            {((filters.categories?.length || 0) > 0 ||
              (filters.tags?.length || 0) > 0 ||
              (filters.levels?.length || 0) > 0) && (
              <div className="lg:hidden mb-4 p-3 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-800">
                  <span className="font-medium">
                    {t("courses.activeFilters")}
                  </span>{" "}
                  {(filters.categories?.length || 0) +
                    (filters.tags?.length || 0) +
                    (filters.levels?.length || 0)}{" "}
                  {t("courses.filtersApplied")}
                </p>
              </div>
            )}

            {/* Loading State */}
            {revalidator.state === "loading" && hasAppliedNewFilters && (
              <div className="text-center py-8 md:py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                <p className="mt-2 text-gray-600">{t("courses.loading")}</p>
              </div>
            )}

            {/* Courses Grid */}
            {revalidator.state !== "loading" || !hasAppliedNewFilters ? (
              <div className="space-y-4 md:space-y-6">
                {allCourses.length > 0 ? (
                  <>
                    {allCourses.map((course) => (
                      <CourseCard
                        key={course.id}
                        course={course}
                        isAuthenticated={isAuthenticated}
                        onAddToBasket={handleAddToBasket}
                      />
                    ))}

                    {/* Load More Trigger */}
                    {pagination.hasNextPage && (
                      <div
                        ref={loadMoreRef}
                        className="flex justify-center items-center py-8"
                      >
                        <div className="flex flex-col items-center gap-2">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                          <p className="text-gray-600 text-sm md:text-base">
                            {t("courses.loadingMore")}
                          </p>
                        </div>
                      </div>
                    )}

                    {!pagination.hasNextPage && allCourses.length > 0 && (
                      <div className="text-center py-8">
                        <p className="text-gray-500">
                          {t("courses.allLoaded")}
                        </p>
                      </div>
                    )}
                  </>
                ) : hasActiveFilters() ? (
                  <div className="text-center py-12 md:py-16">
                    <div className="max-w-md mx-auto">
                      <p className="text-gray-600 text-lg mb-4">
                        {t("courses.noCoursesFound")}
                      </p>
                      <button
                        onClick={resetFilters}
                        className="text-blue-600 hover:underline font-medium"
                      >
                        {t("courses.clearFilters")}
                      </button>
                    </div>
                  </div>
                ) : (
                  <Card className="max-w-md mx-auto text-center bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
                    <CardContent className="py-12">
                      <BookOpen className="w-16 h-16 mx-auto mb-4 text-gray-400 dark:text-gray-500" />
                      <h2 className="text-xl font-semibold mb-2 text-gray-900 dark:text-white">
                        {t("courses.noCourses")}
                      </h2>
                      <p className="text-gray-600 dark:text-gray-400">
                        {t("courses.noCoursesInSystem")}
                      </p>
                    </CardContent>
                  </Card>
                )}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
